import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import type { Dayjs } from 'dayjs';

export type DateBasis = 'transactionDate' | 'applicationDate';

export type DateRangeValue = {
	dateBasis: DateBasis;
	transactionStartDate: Dayjs | null;
	transactionEndDate: Dayjs | null;
	applicationStartDate: Dayjs | null;
	applicationEndDate: Dayjs | null;
	includeSecondaryDateRange: boolean;
};

type Props = {
	value: DateRangeValue;
	onChange: (value: DateRangeValue) => void;
	showTransactionDate?: boolean;
	showApplicationDate?: boolean;
};

const dateBasisLabels: Record<DateBasis, string> = {
	transactionDate: 'Transaction date',
	applicationDate: 'Application date',
};

const getSecondaryDateBasis = (dateBasis: DateBasis): DateBasis => {
	if (dateBasis === 'transactionDate') {
		return 'applicationDate';
	}

	return 'transactionDate';
};

const getPrimaryDateBasis = (
	value: DateRangeValue,
	showTransactionDate: boolean,
	showApplicationDate: boolean,
): DateBasis => {
	if (showTransactionDate && showApplicationDate) {
		return value.dateBasis;
	}

	return showTransactionDate ? 'transactionDate' : 'applicationDate';
};

const clearDateRange = (dateBasis: DateBasis): Partial<DateRangeValue> => {
	if (dateBasis === 'transactionDate') {
		return {
			transactionStartDate: null,
			transactionEndDate: null,
		};
	}

	return {
		applicationStartDate: null,
		applicationEndDate: null,
	};
};

export const DateRangeFilter = ({ value, onChange, showTransactionDate = true, showApplicationDate = true }: Props) => {
	const showBothDateBases = showTransactionDate && showApplicationDate;
	const primaryDateBasis = getPrimaryDateBasis(value, showTransactionDate, showApplicationDate);
	const secondaryDateBasis = getSecondaryDateBasis(primaryDateBasis);

	const updateValue = (changes: Partial<DateRangeValue>) => {
		onChange({
			...value,
			...changes,
		});
	};

	const renderDateRange = (dateBasis: DateBasis, required: boolean) => {
		const isTransactionDate = dateBasis === 'transactionDate';

		return (
			<Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
				<DatePicker<Dayjs>
					label={`${dateBasisLabels[dateBasis]} start${required ? ' *' : ''}`}
					value={isTransactionDate ? value.transactionStartDate : value.applicationStartDate}
					onChange={(date) => {
						updateValue(
							isTransactionDate ? { transactionStartDate: date } : { applicationStartDate: date },
						);
					}}
					slotProps={{ textField: { fullWidth: true } }}
				/>

				<DatePicker<Dayjs>
					label={`${dateBasisLabels[dateBasis]} end`}
					value={isTransactionDate ? value.transactionEndDate : value.applicationEndDate}
					onChange={(date) => {
						updateValue(isTransactionDate ? { transactionEndDate: date } : { applicationEndDate: date });
					}}
					slotProps={{ textField: { fullWidth: true } }}
				/>
			</Stack>
		);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Stack spacing={2}>
				<Typography variant='subtitle1' fontWeight={600}>
					Date range
				</Typography>

				{showBothDateBases && (
					<ToggleButtonGroup
						exclusive
						value={value.dateBasis}
						onChange={(_, dateBasis: DateBasis | null) => {
							if (!dateBasis) {
								return;
							}

							updateValue({
								...clearDateRange(value.dateBasis),
								dateBasis,
								includeSecondaryDateRange: false,
							});
						}}
						size='small'
					>
						<ToggleButton value='transactionDate'>Transaction date</ToggleButton>
						<ToggleButton value='applicationDate'>Application date</ToggleButton>
					</ToggleButtonGroup>
				)}

				{renderDateRange(primaryDateBasis, true)}

				{showBothDateBases && (
					<FormControlLabel
						control={
							<Checkbox
								checked={value.includeSecondaryDateRange}
								onChange={(event) => {
									updateValue({
										...(event.target.checked ? {} : clearDateRange(secondaryDateBasis)),
										includeSecondaryDateRange: event.target.checked,
									});
								}}
							/>
						}
						label={`Add ${dateBasisLabels[secondaryDateBasis].toLowerCase()} filter`}
					/>
				)}

				{showBothDateBases && value.includeSecondaryDateRange && renderDateRange(secondaryDateBasis, false)}
			</Stack>
		</LocalizationProvider>
	);
};
